import { Badge, cn } from "@devhop/ui";
import {
  Building2Icon,
  CalendarDaysIcon,
  HandshakeIcon,
  PackageIcon,
} from "lucide-react";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import type { CreditInvoiceListItemDTO } from "../api/client";
import {
  creditInvoiceStatusBadgeStyle,
  formatCreditInvoiceStatusLa,
} from "../lib/credit-invoice-status";

function rowMuted(cancelled: boolean, className?: string) {
  return cn(cancelled && "opacity-55", className);
}

export function creditInvoiceRefTypeLabel(refType: string): string {
  switch (refType) {
    case "room_booking":
      return "ຈອງຫ້ອງ";
    case "package_purchase":
      return "ແພັກເກດຄລາດສ໌";
    case "private_lesson_package":
      return "ແພັກເກດຮຽນສ່ວນຕົວ";
    case "partner_project_sale":
      return "Partner";
    default:
      return refType;
  }
}

function refTypeBadgeClass(refType: string): string {
  switch (refType) {
    case "room_booking":
      return "border-orange-500/35 bg-orange-500/10 text-orange-900";
    case "package_purchase":
      return "border-blue-500/35 bg-blue-500/10 text-blue-800";
    case "private_lesson_package":
      return "border-violet-500/35 bg-violet-500/10 text-violet-800";
    case "partner_project_sale":
      return "border-slate-500/35 bg-muted/80 text-foreground";
    default:
      return "";
  }
}

export function CreditInvoiceRefTypeBadge({ refType }: { refType: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "max-w-full truncate font-normal text-[11px]",
        refTypeBadgeClass(refType),
      )}
    >
      {creditInvoiceRefTypeLabel(refType)}
    </Badge>
  );
}

function contactInitial(name: string): string {
  const t = name.trim();
  if (!t) return "?";
  return t.charAt(0).toUpperCase();
}

export function CreditInvoiceContactCell({
  row,
}: {
  row: CreditInvoiceListItemDTO;
}) {
  const cancelled = row.status === "cancelled";
  const name = row.contactName ?? row.studentName;
  const sub = row.contactPhone ?? row.studentMemberId ?? null;

  if (!name && !sub) {
    return (
      <span className={rowMuted(cancelled, "text-muted-foreground text-sm")}>
        —
      </span>
    );
  }

  const displayName = name ?? "—";

  return (
    <div
      className={rowMuted(
        cancelled,
        "flex min-w-0 items-center gap-2.5 py-0.5",
      )}
    >
      <div
        className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/50 font-semibold text-muted-foreground text-xs"
        aria-hidden
      >
        {contactInitial(displayName)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-sm leading-snug">
          {displayName}
        </p>
        {sub ? (
          <p className="truncate text-muted-foreground text-xs">{sub}</p>
        ) : null}
      </div>
    </div>
  );
}

function ContextIcon({ refType }: { refType: string }) {
  const className = "size-3.5 shrink-0 text-muted-foreground";
  switch (refType) {
    case "room_booking":
      return <Building2Icon className={className} aria-hidden />;
    case "package_purchase":
    case "private_lesson_package":
      return <PackageIcon className={className} aria-hidden />;
    case "partner_project_sale":
      return <HandshakeIcon className={className} aria-hidden />;
    default:
      return <PackageIcon className={className} aria-hidden />;
  }
}

export function CreditInvoiceContextCell({
  row,
}: {
  row: CreditInvoiceListItemDTO;
}) {
  const cancelled = row.status === "cancelled";
  const title = row.roomName ?? row.packageContextLabel;

  return (
    <div
      className={rowMuted(cancelled, "flex min-w-0 flex-col gap-1.5 py-0.5")}
    >
      <CreditInvoiceRefTypeBadge refType={row.refType} />
      {title ? (
        <div className="flex min-w-0 items-start gap-1.5">
          <ContextIcon refType={row.refType} />
          <span className="line-clamp-2 text-sm leading-snug">{title}</span>
        </div>
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      )}
    </div>
  );
}

export function CreditInvoiceIssuedAtCell({
  row,
}: {
  row: CreditInvoiceListItemDTO;
}) {
  const cancelled = row.status === "cancelled";
  return (
    <div
      className={rowMuted(
        cancelled,
        "flex flex-col gap-0.5 tabular-nums text-sm",
      )}
    >
      <span className="font-medium">{formatDate(row.issuedAt)}</span>
      <span className="text-muted-foreground text-xs">
        {new Date(row.issuedAt).toLocaleTimeString("lo-LA", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    </div>
  );
}

export function CreditInvoiceBookingCell({
  row,
}: {
  row: CreditInvoiceListItemDTO;
}) {
  const cancelled = row.status === "cancelled";
  if (!row.bookingDate) {
    return (
      <span className={rowMuted(cancelled, "text-muted-foreground text-sm")}>
        —
      </span>
    );
  }

  const start = row.bookingStartTime;
  const end = row.bookingEndTime;
  const hasTime = Boolean(start && end);

  return (
    <div
      className={rowMuted(
        cancelled,
        "flex items-start gap-2 text-sm tabular-nums",
      )}
    >
      <CalendarDaysIcon
        className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
        aria-hidden
      />
      <div className="flex flex-col gap-0.5">
        <span>{formatDate(row.bookingDate)}</span>
        {hasTime && start && end ? (
          <span className="text-muted-foreground text-xs">
            {formatTime(start)} – {formatTime(end)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

type AmountTone = "total" | "paid" | "remaining";

export function CreditInvoiceAmountCell({
  amount,
  tone,
  cancelled,
}: {
  amount: number;
  tone: AmountTone;
  cancelled?: boolean;
}) {
  const formatted = amount.toLocaleString("lo-LA");

  const valueClass = cn(
    "tabular-nums text-sm leading-none",
    tone === "total" && "text-foreground",
    tone === "paid" && "text-emerald-700 dark:text-emerald-400",
    tone === "remaining" &&
      (amount > 0
        ? "font-semibold text-amber-800 dark:text-amber-300"
        : "text-muted-foreground"),
    cancelled && "opacity-55",
  );

  return (
    <div className="flex justify-end py-0.5">
      <span className={valueClass}>
        {formatted}
        <span className="ml-0.5 font-normal text-[11px] text-muted-foreground">
          ກີບ
        </span>
      </span>
    </div>
  );
}

export function CreditInvoiceStatusCell({
  row,
}: {
  row: CreditInvoiceListItemDTO;
}) {
  const { variant, className } = creditInvoiceStatusBadgeStyle(row.status);
  return (
    <Badge variant={variant} className={cn("whitespace-nowrap", className)}>
      {formatCreditInvoiceStatusLa(row.status)}
    </Badge>
  );
}
